import React, { useCallback, useEffect, useRef, useState } from "react";
import { FiX, FiTrash2, FiLoader, FiFileText, FiInfo, FiCopy, FiCheck, FiClipboard, FiUploadCloud, FiSend, FiPlus, FiMessageSquare, FiRotateCcw } from "react-icons/fi";
import { apiRequest, errorMessage } from "../api";
import { getClientId, formatSize } from "./audioShared";

const ACCEPT = ".pdf,.docx,.pptx,.odt,.html,.htm,.txt,.md,.csv";
const MAX_FILE_BYTES = 25 * 1024 * 1024;
const MAX_PASTED = 200000;
const MAX_QUESTION = 1000;
const CITATION = /(\[p\.\d+(?:,\s*p\.\d+)*\]|\*\*[^*\n]+\*\*)/g;

async function copyText(text) {
  try {
    await navigator.clipboard.writeText(text);
    return true;
  } catch {
    const area = document.createElement("textarea");
    area.value = text;
    document.body.appendChild(area);
    area.select();
    const ok = document.execCommand("copy");
    document.body.removeChild(area);
    return ok;
  }
}

const isRtl = (text) => (text.match(/[؀-ۿ֐-׿]/g) || []).length / Math.max(1, text.length) > 0.25;

// One line of an answer: **bold** and [p.3] citations (which open the passage they came from).
function Inline({ text, label, onCite }) {
  return text.split(CITATION).map((part, i) => {
    const cite = part.match(/^\[p\.\d+(?:,\s*p\.\d+)*\]$/);
    if (cite) {
      return (part.match(/\d+/g) || []).map((n) => (
        <button
          key={`${i}-${n}`}
          type="button"
          onClick={() => onCite(Number(n))}
          title={`Show the passage on ${label.toLowerCase()} ${n}`}
          className="mx-0.5 px-1.5 py-0.5 rounded bg-accent/15 border border-accent-light/40 text-accent-light text-[11px] font-semibold align-baseline hover:bg-accent/30"
        >
          {label === "Page" ? "p." : "part "}{n}
        </button>
      ));
    }
    return part.startsWith("**") && part.endsWith("**") ? <strong key={i}>{part.slice(2, -2)}</strong> : <React.Fragment key={i}>{part}</React.Fragment>;
  });
}

function AnswerText({ text, label, onCite }) {
  const lines = text.split("\n").filter((l) => l.trim());
  const blocks = [];
  lines.forEach((line) => {
    const bullet = line.match(/^\s*[-*•]\s+(.*)$/);
    const last = blocks[blocks.length - 1];
    if (bullet) {
      if (last && last.type === "list") last.items.push(bullet[1]);
      else blocks.push({ type: "list", items: [bullet[1]] });
    } else blocks.push({ type: "p", text: line });
  });
  return (
    <div className="space-y-2">
      {blocks.map((b, i) => b.type === "list" ? (
        <ul key={i} className="list-disc ps-5 space-y-1.5 marker:text-accent-light">
          {b.items.map((item, j) => <li key={j} className="leading-relaxed"><Inline text={item} label={label} onCite={onCite} /></li>)}
        </ul>
      ) : (
        <p key={i} className="leading-relaxed"><Inline text={b.text} label={label} onCite={onCite} /></p>
      ))}
    </div>
  );
}

function CopyIcon({ getText }) {
  const [done, setDone] = useState(false);
  return (
    <button
      type="button"
      title="Copy the answer"
      onClick={async () => { if (await copyText(getText())) { setDone(true); setTimeout(() => setDone(false), 1500); } }}
      className="text-gray-500 hover:text-white"
    >
      {done ? <FiCheck className="text-emerald-400" size={14} /> : <FiCopy size={14} />}
    </button>
  );
}

// AI PDF Q&A Chatbot: upload a document, then ask it questions. Answers come from the document only and cite pages.
export default function PdfChatTool() {
  const [clientId] = useState(getClientId);
  const [documents, setDocuments] = useState([]);
  const [loading, setLoading] = useState(true);
  const [active, setActive] = useState(null);          // the open document, with its messages
  const [source, setSource] = useState("file");        // "file" | "text"
  const [file, setFile] = useState(null);
  const [text, setText] = useState("");
  const [dragging, setDragging] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [question, setQuestion] = useState("");
  const [asking, setAsking] = useState(false);
  const [error, setError] = useState("");
  const [fieldError, setFieldError] = useState("");
  const [open, setOpen] = useState({});                // message id -> page whose passage is shown
  const inputRef = useRef(null);
  const endRef = useRef(null);
  const boxRef = useRef(null);

  const query = `client_id=${encodeURIComponent(clientId)}`;
  const label = active?.kind === "part" ? "Part" : "Page";

  const loadDocuments = useCallback(async () => {
    const { ok, data } = await apiRequest(`/core/pdf-chat/?${query}`);
    if (ok) setDocuments(data);
    setLoading(false);
  }, [query]);

  useEffect(() => { loadDocuments(); }, [loadDocuments]);

  useEffect(() => {
    if (boxRef.current) boxRef.current.scrollTop = boxRef.current.scrollHeight;
  }, [active?.messages?.length, asking]);

  const openDocument = async (id) => {
    setError("");
    const { ok, data } = await apiRequest(`/core/pdf-chat/${id}/?${query}`);
    if (ok) { setActive(data); setOpen({}); } else setError(errorMessage(data));
  };

  const pickFile = (picked) => {
    setFieldError("");
    if (!picked) return;
    if (picked.size > MAX_FILE_BYTES) return setFieldError(`This file is larger than ${MAX_FILE_BYTES / 1024 / 1024} MB.`);
    setFile(picked);
  };

  const upload = async () => {
    setError("");
    setFieldError("");
    setUploading(true);
    const form = new FormData();
    form.append("client_id", clientId);
    if (source === "file") form.append("original", file);
    else form.append("text", text);
    const { ok, data } = await apiRequest("/core/pdf-chat/", { method: "POST", body: form });
    setUploading(false);
    if (!ok) {
      if (data?.original || data?.text) return setFieldError(data.original || data.text);
      return setError(errorMessage(data));
    }
    setFile(null);
    setText("");
    setActive(data);
    setOpen({});
    loadDocuments();
    setTimeout(() => inputRef.current?.focus(), 50);
  };

  const ask = async (asked) => {
    const content = (asked ?? question).trim();
    if (!content || asking || !active) return;
    setError("");
    setAsking(true);
    setQuestion("");
    // show the question straight away
    const pending = { id: `pending-${Date.now()}`, role: "user", content, sources: [] };
    setActive((doc) => ({ ...doc, messages: [...doc.messages, pending] }));
    const { ok, data } = await apiRequest(`/core/pdf-chat/${active.id}/ask/`, { method: "POST", body: { client_id: clientId, question: content } });
    setAsking(false);
    if (!ok) {
      setActive((doc) => ({ ...doc, messages: doc.messages.filter((m) => m.id !== pending.id) }));
      setQuestion(content);
      return setError(data?.question || errorMessage(data));
    }
    setActive((doc) => ({ ...doc, messages: [...doc.messages.filter((m) => m.id !== pending.id), data.question, data.answer], message_count: (doc.message_count || 0) + 2 }));
    setDocuments((list) => list.map((d) => (d.id === active.id ? { ...d, message_count: (d.message_count || 0) + 2 } : d)));
  };

  const clearChat = async () => {
    if (!active || !window.confirm("Clear this conversation? The document stays.")) return;
    await apiRequest(`/core/pdf-chat/${active.id}/clear/`, { method: "POST", body: { client_id: clientId } });
    setActive((doc) => ({ ...doc, messages: [], message_count: 0 }));
    setDocuments((list) => list.map((d) => (d.id === active.id ? { ...d, message_count: 0 } : d)));
    setOpen({});
  };

  const removeDocument = async (doc) => {
    if (!window.confirm(`Delete "${doc.name}" and its conversation?`)) return;
    await apiRequest(`/core/pdf-chat/${doc.id}/?${query}`, { method: "DELETE" });
    setDocuments((list) => list.filter((d) => d.id !== doc.id));
    if (active?.id === doc.id) setActive(null);
  };

  const hasInput = source === "file" ? !!file : text.trim().length >= 20;
  const messages = active?.messages || [];

  return (
    <div className="max-w-4xl mx-auto px-6 md:px-12 py-14">
      <h2 className="text-2xl md:text-3xl font-bold text-center mb-3">AI PDF Q&amp;A Chatbot</h2>
      <p className="text-center text-gray-400 max-w-2xl mx-auto mb-10">
        Upload a PDF and chat with it. Ask a question in your own words, and get an answer taken from the document, with the page it came from,
        so you can check it. Works with reports, contracts, manuals, papers and books.
      </p>

      {!active && (
        <>
          <div className="flex justify-center mb-6">
            <div className="inline-flex rounded-full bg-white/5 border border-navy-border p-1">
              {[["file", "Upload a PDF", FiUploadCloud], ["text", "Paste text", FiClipboard]].map(([key, name, Icon]) => (
                <button
                  key={key}
                  type="button"
                  onClick={() => { setSource(key); setFieldError(""); }}
                  className={`px-5 py-2 rounded-full text-sm font-semibold inline-flex items-center gap-2 transition-colors ${source === key ? "bg-brand-gradient text-white shadow-md shadow-accent/20" : "text-gray-300 hover:text-white"}`}
                >
                  <Icon size={15} /> {name}
                </button>
              ))}
            </div>
          </div>

          {source === "file" ? (
            <div className="rounded-2xl bg-white/5 border border-navy-border p-6">
              {!file ? (
                <div
                  onClick={() => inputRef.current?.click()}
                  onDragOver={(e) => { e.preventDefault(); setDragging(true); }}
                  onDragLeave={() => setDragging(false)}
                  onDrop={(e) => { e.preventDefault(); setDragging(false); pickFile(e.dataTransfer.files?.[0]); }}
                  role="button"
                  tabIndex={0}
                  onKeyDown={(e) => (e.key === "Enter" || e.key === " ") && inputRef.current?.click()}
                  className={`cursor-pointer rounded-xl border-2 border-dashed px-4 py-12 text-center transition-colors ${dragging ? "border-accent-light bg-accent/10" : "border-navy-border hover:border-accent-light/70"}`}
                >
                  <FiFileText className="mx-auto text-accent-light" size={32} />
                  <p className="mt-3 text-sm text-gray-200">Click to choose a PDF, or drop it here</p>
                  <p className="text-xs text-gray-500 mt-1">PDF, Word, PowerPoint, OpenDocument, HTML or TXT - up to 25 MB</p>
                </div>
              ) : (
                <div className="flex items-center justify-between gap-3 rounded-xl bg-navy border border-navy-border p-4">
                  <div className="flex items-center gap-3 min-w-0">
                    <FiFileText className="text-accent-light shrink-0" size={22} />
                    <div className="min-w-0">
                      <p className="text-sm font-medium truncate">{file.name}</p>
                      <p className="text-xs text-gray-500">{formatSize(file.size)}</p>
                    </div>
                  </div>
                  <button type="button" onClick={() => { setFile(null); setFieldError(""); }} aria-label="Remove document" className="text-gray-400 hover:text-red-400 shrink-0"><FiX size={18} /></button>
                </div>
              )}
              <input ref={inputRef} type="file" accept={ACCEPT} className="hidden" onChange={(e) => { pickFile(e.target.files?.[0]); e.target.value = ""; }} />
              <p className="text-xs text-gray-500 mt-3">Scanned PDFs (pictures of pages) have no text to read. Use a text-based PDF, or paste the text.</p>
            </div>
          ) : (
            <div className="rounded-2xl bg-white/5 border border-navy-border p-6">
              <div className="flex items-center justify-between mb-3">
                <label htmlFor="pdfchat-text" className="text-xs uppercase tracking-widest text-white/50 font-semibold">Your text</label>
                <span className="text-xs text-gray-500">{text.length.toLocaleString()} / {MAX_PASTED.toLocaleString()} characters</span>
              </div>
              <textarea
                id="pdfchat-text"
                value={text}
                onChange={(e) => setText(e.target.value.slice(0, MAX_PASTED))}
                placeholder="Paste the text you want to ask questions about..."
                className="w-full min-h-[220px] px-5 py-4 rounded-xl bg-navy border border-navy-border text-white text-base leading-relaxed focus:outline-none focus:border-accent-light resize-y"
              />
            </div>
          )}
          {fieldError && <p className="text-sm text-amber-300 mt-3">{fieldError}</p>}
          {error && <p className="text-sm text-red-400 bg-red-500/10 border border-red-500/30 rounded-xl px-5 py-4 mt-4">{error}</p>}

          <button
            onClick={upload}
            disabled={!hasInput || uploading}
            className="w-full mt-6 flex items-center justify-center gap-2 px-6 py-4 rounded-full bg-brand-gradient text-white font-semibold text-base shadow-lg shadow-accent/20 disabled:opacity-50 disabled:cursor-not-allowed"
          >
            {uploading ? <><FiLoader className="animate-spin" /> Reading the document...</> : <><FiMessageSquare /> Start chatting</>}
          </button>

          <div className="mt-10">
            <p className="text-xs uppercase tracking-widest text-white/50 font-semibold mb-4">Your documents</p>
            {loading ? (
              <p className="text-sm text-gray-400">Loading...</p>
            ) : documents.length === 0 ? (
              <p className="text-sm text-gray-500">Nothing here yet. Documents you upload stay here so you can come back to the conversation.</p>
            ) : (
              <div className="grid sm:grid-cols-2 gap-4">
                {documents.map((doc) => (
                  <div key={doc.id} className="rounded-xl bg-white/5 border border-navy-border p-4">
                    <p className="text-sm text-gray-200 truncate">{doc.name}</p>
                    <p className="text-xs text-gray-500 mb-3">
                      {doc.page_count} {doc.kind === "part" ? "parts" : "pages"} · {doc.word_count.toLocaleString()} words · {doc.message_count / 2 | 0} questions
                    </p>
                    <div className="flex items-center justify-between gap-3">
                      <button type="button" onClick={() => openDocument(doc.id)} className="text-sm text-accent-light hover:underline">Open chat</button>
                      <button onClick={() => removeDocument(doc)} title="Delete" className="text-gray-400 hover:text-red-400 shrink-0"><FiTrash2 size={16} /></button>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        </>
      )}

      {active && (
        <div className="rounded-2xl bg-white/5 border border-navy-border overflow-hidden">
          <div className="flex flex-wrap items-center justify-between gap-3 px-5 py-4 border-b border-navy-border">
            <div className="flex items-center gap-3 min-w-0">
              <FiFileText className="text-accent-light shrink-0" size={22} />
              <div className="min-w-0">
                <p className="font-semibold truncate">{active.name}</p>
                <p className="text-xs text-gray-500">{active.page_count} {active.kind === "part" ? "parts" : "pages"} · {active.word_count.toLocaleString()} words</p>
              </div>
            </div>
            <div className="flex items-center gap-2">
              {messages.length > 0 && (
                <button type="button" onClick={clearChat} className="px-3 py-1.5 rounded-full border border-white/20 hover:border-accent-light text-xs font-medium inline-flex items-center gap-1.5"><FiRotateCcw size={13} /> Clear chat</button>
              )}
              <button type="button" onClick={() => { setActive(null); setError(""); loadDocuments(); }} className="px-3 py-1.5 rounded-full border border-white/20 hover:border-accent-light text-xs font-medium inline-flex items-center gap-1.5"><FiPlus size={13} /> Another document</button>
            </div>
          </div>

          {active.notice && <p className="text-xs text-amber-200 bg-amber-500/10 border-b border-amber-500/30 px-5 py-2.5">{active.notice}</p>}

          <div ref={boxRef} className="px-5 py-5 space-y-5 min-h-[260px] max-h-[540px] overflow-y-auto">
            {messages.length === 0 && (
              <div className="text-center py-6">
                <FiMessageSquare className="mx-auto text-accent-light" size={30} />
                <p className="mt-3 text-sm text-gray-300">The document is ready. Ask anything about it, in any language.</p>
                <div className="flex flex-wrap justify-center gap-2 mt-5">
                  {(active.suggestions || []).map((s) => (
                    <button key={s} type="button" onClick={() => ask(s)} disabled={asking} className="px-4 py-2 rounded-full bg-white/5 border border-navy-border hover:border-accent-light text-sm text-gray-200 transition-colors">
                      {s}
                    </button>
                  ))}
                </div>
              </div>
            )}

            {messages.map((m) => m.role === "user" ? (
              <div key={m.id} className="flex justify-end">
                <p dir={isRtl(m.content) ? "rtl" : "ltr"} className="max-w-[85%] rounded-2xl rounded-br-sm bg-brand-gradient text-white px-4 py-2.5 whitespace-pre-line">{m.content}</p>
              </div>
            ) : (
              <div key={m.id} className="flex justify-start">
                <div className={`max-w-[92%] rounded-2xl rounded-bl-sm border px-4 py-3 ${m.found === false ? "bg-navy border-navy-border text-gray-300" : "bg-navy border-accent-light/30 text-gray-100"}`}>
                  <div dir={isRtl(m.content) ? "rtl" : "ltr"}>
                    <AnswerText text={m.content} label={label} onCite={(page) => setOpen((o) => ({ ...o, [m.id]: o[m.id] === page ? null : page }))} />
                  </div>
                  {m.sources?.length > 0 && (
                    <div className="mt-3 flex flex-wrap items-center gap-2">
                      <span className="text-[11px] uppercase tracking-widest text-white/40">Sources</span>
                      {m.sources.map((s) => (
                        <button
                          key={s.page}
                          type="button"
                          onClick={() => setOpen((o) => ({ ...o, [m.id]: o[m.id] === s.page ? null : s.page }))}
                          className={`px-2.5 py-0.5 rounded-full border text-xs font-medium transition-colors ${open[m.id] === s.page ? "bg-accent-light text-navy border-accent-light" : "border-white/20 text-gray-300 hover:border-accent-light"}`}
                        >
                          {label} {s.page}
                        </button>
                      ))}
                    </div>
                  )}
                  {open[m.id] && (() => {
                    const passage = m.sources?.find((s) => s.page === open[m.id]);
                    return (
                      <blockquote className="mt-3 border-s-2 border-accent-light ps-3 text-xs text-gray-400 leading-relaxed" dir="auto">
                        <span className="block text-[11px] uppercase tracking-widest text-white/40 mb-1">From {label.toLowerCase()} {open[m.id]}</span>
                        {passage ? passage.text : `This ${label.toLowerCase()} was used for the answer.`}
                      </blockquote>
                    );
                  })()}
                  <div className="mt-2 flex items-center justify-between gap-4">
                    <span className="text-[11px] text-gray-500">
                      {m.found === false ? "Not found in the document" : m.engine === "Document search" ? "Quoted from the document (no AI writer was available)" : m.engine ? `Written by ${m.engine === "Free AI" ? "a free AI model" : m.engine}` : ""}
                    </span>
                    <CopyIcon getText={() => m.content} />
                  </div>
                </div>
              </div>
            ))}

            {asking && (
              <div className="flex justify-start">
                <div className="rounded-2xl rounded-bl-sm bg-navy border border-navy-border px-4 py-3 text-sm text-gray-400 inline-flex items-center gap-2">
                  <FiLoader className="animate-spin text-accent-light" /> Searching the document...
                </div>
              </div>
            )}
            <div ref={endRef} />
          </div>

          {error && <p className="text-sm text-red-400 bg-red-500/10 border-t border-red-500/30 px-5 py-3 flex items-start gap-2"><FiInfo className="mt-0.5 shrink-0" />{error}</p>}

          <form
            onSubmit={(e) => { e.preventDefault(); ask(); }}
            className="flex items-end gap-3 px-5 py-4 border-t border-navy-border"
          >
            <textarea
              ref={inputRef}
              value={question}
              onChange={(e) => setQuestion(e.target.value.slice(0, MAX_QUESTION))}
              onKeyDown={(e) => { if (e.key === "Enter" && !e.shiftKey) { e.preventDefault(); ask(); } }}
              rows={1}
              placeholder="Ask a question about this document..."
              aria-label="Your question"
              dir="auto"
              className="flex-1 resize-none max-h-32 px-4 py-3 rounded-xl bg-navy border border-navy-border text-white text-sm focus:outline-none focus:border-accent-light"
            />
            <button
              type="submit"
              disabled={!question.trim() || asking}
              aria-label="Send question"
              className="shrink-0 w-11 h-11 rounded-full bg-brand-gradient text-white inline-flex items-center justify-center shadow-md shadow-accent/20 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {asking ? <FiLoader className="animate-spin" /> : <FiSend />}
            </button>
          </form>
        </div>
      )}

      <p className="text-xs text-gray-500 text-center mt-6 max-w-2xl mx-auto">
        Answers are written only from the document you uploaded. Click a page tag to see the passage an answer is based on, and check
        important details (amounts, dates, legal terms) against the original.
      </p>
    </div>
  );
}
